import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { sectionRenderer, morphSection } from '@theme/section-renderer';

/**
 * Swaps the server-rendered placeholders of a product-list section in
 * "Shopify recommendations" mode for the actual recommended products.
 *
 * On the cart page the section is seeded with the first cart line instead of a viewed
 * product, and everything already in the cart is filtered out of the answer — see
 * #withoutCartProducts.
 *
 * The `recommendations` Liquid object is only populated when the section is
 * rendered through the /recommendations/products endpoint, so the first paint
 * can't show real products. We re-request the same section via that endpoint
 * (product_id + intent) — its Liquid then loops `recommendations.products` and
 * renders the same `_product-card` blocks used everywhere else — and morph the
 * response into the live DOM. Same pattern as recently-viewed-section.js.
 */
class ProductListRecommendations extends Component {
  /**
   * Defer the fetch until the section approaches the viewport (it sits below
   * the product details).
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Whether this list is seeded from the cart (cart page) instead of a viewed product.
   * Captured once, at construction: the /recommendations/products render context always has a
   * `product` (the seed itself), so the morphed-in markup reports `false` and the flag would be
   * lost after the first swap.
   * @type {boolean}
   */
  #cartSeeded = this.dataset.cartSeeded === 'true';

  /**
   * True when the section root is hidden on first paint, i.e. the server had nothing to render.
   * Walks up to the section root because this element is display:contents and carries no box.
   * @returns {boolean}
   */
  #startsHidden() {
    const root = this.closest('[data-testid="product-list"]');
    if (!root) return false;
    return root.hasAttribute('hidden') || root.style.display === 'none';
  }

  connectedCallback() {
    super.connectedCallback();

    // A section in recommendations mode with NO fallback collection renders empty on the
    // server, and product-list.liquid then paints it `hidden` + `display: none`. An
    // IntersectionObserver can never fire inside a display:none subtree, so the fetch would
    // never run and the section would stay invisible forever - which is exactly what happened
    // to "Ähnliche Produkte" on live (131251, 2026-09-02) the moment the source was switched
    // to Shopify recommendations. When we start out hidden there is nothing to scroll into
    // view, so load immediately instead; the section reveals itself once the products arrive.
    if (this.#startsHidden()) {
      this.#loadRecommendations();
      return;
    }

    // The element is display:contents (it must not break the section grid), so it has
    // no box of its own — observe the rendered list inside it instead.
    this.#intersectionObserver.observe(this.firstElementChild ?? this);

    // Cart page only: line changes morph the cart section alone, so this list would keep
    // recommending against the cart as it was on page load.
    if (this.#cartSeeded) {
      document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * Set while a cart-driven refresh is running, so cart updates that land back to back queue
   * up behind each other instead of stacking two section renders onto the same list.
   * @type {boolean}
   */
  #cartRefreshInFlight = false;

  /**
   * Re-seeds the list from the current cart and re-runs the request, so a product added or
   * removed on the cart page is reflected in what gets recommended.
   * @param {Event} [event] - The cart:update event that triggered the refresh.
   * @type {(event?: Event) => Promise<void>}
   */
  #handleCartUpdate = async (event) => {
    // A cart operation that FAILED still dispatches cart:update (see CartUpdateEvent in
    // events.js) - the cart is unchanged, so there is nothing to re-request.
    if (/** @type {any} */ (event)?.detail?.data?.didError) return;
    if (this.#cartRefreshInFlight) return;

    try {
      const cartUrl = globalThis.Theme?.routes?.cart_url || '/cart';
      const cart = await fetch(`${cartUrl}.js`)
        .then((response) => response.json())
        .catch(() => null);

      const productIds = cart?.items?.map((item) => String(item.product_id)) ?? [];

      // Empty cart: nothing to seed with. Keep the list as it stands rather than blanking it —
      // a reload renders the chosen-collection fallback the Liquid falls back to.
      if (!productIds.length) return;

      const seedId = productIds[0] ?? '';
      const excludeIds = `,${productIds.join(',')},`;

      // 45777: what this list shows depends ONLY on the seed product and the set of products in
      // the cart. Quantity changes, discount/total refreshes and any other cart:update that
      // leaves those two untouched would re-render the section into byte-identical markup, so
      // skip the round trip entirely. `performed` guards the case where the list has not had a
      // successful recommendations render yet - then the refetch is exactly what is needed.
      if (this.dataset.performed === 'true' && seedId === this.dataset.productId && excludeIds === this.dataset.excludeIds) {
        return;
      }

      this.#cartRefreshInFlight = true;
      this.dataset.productId = seedId;
      this.dataset.excludeIds = excludeIds;
      // Same as the Liquid: always ask for the endpoint's maximum, because the cart lines AND the
      // hidden / `exclude` / side products (132736) get filtered out of the answer server-side.
      this.dataset.limit = '10';
      this.dataset.performed = 'false';

      await this.#loadRecommendations();
    } catch (error) {
      // A failed refresh must never surface: the list simply keeps its last good state.
      console.error('Product recommendations cart refresh error:', error);
    } finally {
      this.#cartRefreshInFlight = false;
    }
  };

  async #loadRecommendations() {
    const { sectionId, productId, limit, intent, recsUrl, performed } = this.dataset;

    // The morphed-in markup was rendered with recommendations.performed=true —
    // don't re-fetch if this element reconnects (e.g. after another morph).
    if (performed === 'true' || !sectionId || !productId) return;

    const url = new URL(recsUrl || '/recommendations/products', location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', limit || '8');
    url.searchParams.set('intent', intent || 'related');

    try {
      const sectionHTML = await sectionRenderer.getSectionHTML(sectionId, false, url);

      if (sectionHTML?.trim().length) {
        // Full morph: when the engine returns no products the response root
        // carries `hidden`, so the swap also hides the section.
        await morphSection(sectionId, this.#withoutCartProducts(sectionHTML), { injectStylesheet: true });

        // Re-stamp the flag the response can't know about, so an element re-created from this
        // markup still refreshes itself on cart changes.
        if (this.#cartSeeded) this.dataset.cartSeeded = 'true';
      }
    } catch (error) {
      // In the Theme Editor a freshly added section's id may not be resolvable by
      // the Section Rendering API yet — keep the placeholders instead of erroring.
      if (!Shopify.designMode) {
        console.error('Product recommendations error:', error);
      }
    }
  }

  /**
   * Second line of defence for the cart page. The section Liquid already drops products that sit
   * in the cart, but that filter runs in the /recommendations/products render context — re-apply
   * it here on the returned markup so a card for something already in the cart can never reach
   * the DOM, and trim back to the configured product count (the request asks for spares).
   *
   * @param {string} html - Section markup returned by the Section Rendering API.
   * @returns {string} The same markup without the cart's own products.
   */
  #withoutCartProducts(html) {
    try {
      const excluded = (this.dataset.excludeIds ?? '').split(',').filter(Boolean);
      if (!excluded.length) return html;

      const maxItems = Number(this.dataset.maxItems) || 0;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = Array.from(doc.querySelectorAll('.resource-list__item'));
      if (!items.length) return html;

      let kept = 0;

      for (const item of items) {
        const productId = item.querySelector('product-card[data-product-id]')?.dataset.productId;

        if ((productId && excluded.includes(productId)) || (maxItems && kept >= maxItems)) {
          // In carousel mode each item is wrapped in a slide — drop the wrapper too, or the
          // slideshow keeps an empty page where the card was.
          (item.closest('slideshow-slide') ?? item).remove();
        } else {
          kept++;
        }
      }

      // Everything the engine returned is already in the cart: hide the section, same as the
      // no-results path Liquid renders (attribute + inline display, mirroring the Liquid).
      if (kept === 0) {
        const root = doc.querySelector('[data-testid="product-list"]');
        root?.setAttribute('hidden', '');
        if (root) root.style.display = 'none';
      }

      // The response opens with the section's `<style data-section-stylesheet>`, which the HTML
      // parser puts in <head> — serialise both halves so the morph still gets it.
      return doc.head.innerHTML + doc.body.innerHTML;
    } catch (error) {
      // Filtering is defence in depth — the server already excluded the cart's products, so on
      // any unexpected failure the unfiltered response is still safe to morph in.
      console.error('Product recommendations filter error:', error);
      return html;
    }
  }
}

if (!customElements.get('product-list-recommendations')) {
  customElements.define('product-list-recommendations', ProductListRecommendations);
}
